import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

export interface StepperProps {
  valor: number;
  onCambiar: (valor: number) => void;
  min: number;
  max: number;
  /** Incremento por click. Por defecto 1. */
  paso?: number;
  /** Texto corto después del número, ej. `"preg."`. */
  sufijo?: string;
  disabled?: boolean;
  /** Por defecto el contador mide lo que ocupan sus tres celdas (`w-fit`).
   *  Pasá `w-full` para que llene la columna: la celda del valor crece y los
   *  dos botones se quedan en 34px, que es lo que pide una fila de controles
   *  parejos (ej. select · select · contador). */
  className?: string;
  "aria-label": string;
}

/**
 * Contador +/- del Design System: botón remove · valor mono centrado ·
 * botón add, dentro de un borde compartido. Usado para cantidades chicas
 * acotadas (preguntas por temática, 2–15) donde un slider sería exagerado.
 */
export function Stepper({
  valor,
  onCambiar,
  min,
  max,
  paso = 1,
  sufijo,
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: StepperProps) {
  const bajar = () => onCambiar(Math.max(min, valor - paso));
  const subir = () => onCambiar(Math.min(max, valor + paso));

  return (
    <div
      className={cn(
        "border-input flex h-[34px] w-fit items-stretch overflow-hidden rounded-md border",
        disabled && "opacity-50",
        className
      )}
    >
      <button
        type="button"
        onClick={bajar}
        disabled={disabled || valor <= min}
        aria-label={`Disminuir ${ariaLabel}`}
        className="hover:bg-border-faint flex w-[34px] items-center justify-center text-muted-foreground transition-colors disabled:pointer-events-none disabled:opacity-40"
      >
        <Icon name="remove" size={16} />
      </button>
      <div
        role="status"
        aria-label={ariaLabel}
        className="border-border-subtle fc-num flex min-w-[42px] flex-1 items-center justify-center border-r border-l px-1.5 text-[13.5px] font-medium"
      >
        {valor}
        {sufijo && <span className="text-soft ml-1 text-[11px] font-normal">{sufijo}</span>}
      </div>
      <button
        type="button"
        onClick={subir}
        disabled={disabled || valor >= max}
        aria-label={`Aumentar ${ariaLabel}`}
        className="hover:bg-border-faint flex w-[34px] items-center justify-center text-muted-foreground transition-colors disabled:pointer-events-none disabled:opacity-40"
      >
        <Icon name="add" size={16} />
      </button>
    </div>
  );
}
